import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { products } from '../data/mock';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

export const ProductImage = ({ src, alt, title, width = 800, height = 600 }) => {
  return (
    <img
      src={src}
      alt={alt}
      title={title}
      loading="lazy"
      decoding="async"
      width={width}
      height={height}
      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
    />
  );
};

const Products = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <section id="products" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-blue-900">Product Range</span>
          </h2>
          <div className="w-24 h-1 bg-blue-900 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive lighting solutions engineered for excellence, durability, and optimal performance across diverse applications
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 mb-16 px-4">
          {products.map((product) => (
            <Card
              key={product.id}
              className="group bg-white rounded-3xl overflow-hidden hover:shadow-[0_15px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 border border-gray-100 hover:border-blue-100 hover:-translate-y-2"
            >
              <div className="relative overflow-hidden h-64">
                <ProductImage
                  src={product.image}
                  alt={product.title}
                  title={product.category}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-black/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500"></div>
                <Badge className="absolute top-5 right-5 bg-white/90 backdrop-blur-md text-blue-900 border-none py-1.5 px-3 text-xs tracking-wider uppercase font-bold shadow-lg hover:bg-white group-hover:scale-105 transition-transform duration-300">
                  {product.category}
                </Badge>
              </div>
              <CardHeader>
                <CardTitle className="text-xl group-hover:text-blue-900 transition-colors">
                  {product.title}
                </CardTitle>
                <CardDescription className="line-clamp-2">
                  {product.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  {product.features.slice(0, 3).map((feature, index) => (
                    <div key={index} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle2 size={16} className="text-blue-900 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center bg-gradient-to-br from-blue-50 via-white to-blue-50 border border-blue-100/50 rounded-3xl p-12 mt-16 shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Need a Custom Solution?
          </h3>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            We specialize in developing customized designed products tailored to meet the unique requirements of your specialized projects.
          </p>
          <Button
            size="lg"
            className="bg-blue-900 hover:bg-blue-800 text-white px-10 py-7 text-lg font-bold rounded-xl shadow-[0_4px_14px_0_rgba(30,58,138,0.39)] hover:shadow-[0_6px_20px_rgba(30,58,138,0.23)] hover:-translate-y-1 transition-all duration-300"
            onClick={scrollToContact}
          >
            Discuss Your Requirements
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Products;
